/**
 * CHAPITRES DU CHEMINEMENT.
 *
 * CE QU'ILS REMPLACENT. Le panneau annonçait « Étape 5 sur 7 ». Le compte est
 * exact et ne dit rien : ni où l'on est dans le propos, ni ce qui vient de se
 * terminer, ni combien il reste avant la pause. Un module d'intro territoire
 * n'est pas une liste d'écrans, c'est une suite de sujets — le grand cycle de
 * l'eau, puis le climat, puis les usages — et c'est ce découpage-là que la
 * salle suit.
 *
 * COMMENT ILS SONT ÉCRITS. Chaque étape porte un simple `chapter` : le nom du
 * sujet, ou rien. Les étapes QUI SE SUIVENT avec le même nom forment un
 * chapitre. Il n'y a pas de liste de chapitres à côté de la liste d'étapes.
 *
 * C'est un choix, et il se défend : une liste séparée devrait référencer les
 * étapes par identifiant, donc être maintenue d'accord avec elles à chaque
 * ajout, suppression ou glisser-déposer dans le storyboard — trois endroits où
 * l'on peut désynchroniser une structure que personne ne regarde. Avec un nom
 * porté par l'étape, réordonner LES étapes réordonne LES chapitres, et une
 * étape déplacée hors de son groupe change de chapitre : c'est ce qu'on voit à
 * l'écran, donc ce qu'on veut dire.
 *
 * Le prix de ce choix : deux groupes non adjacents portant le même nom sont
 * deux chapitres distincts. C'est voulu — revenir au climat après un détour par
 * les usages, c'est bien un second passage, et l'avancement doit le montrer.
 */

// Proposés à l'auteur dans le builder. Ce ne sont pas des valeurs imposées —
// le champ reste libre — mais les six sujets qui reviennent d'un territoire à
// l'autre, offerts en un clic pour que le découpage soit fait plutôt que remis
// à plus tard.
export const SUGGESTED_CHAPTERS = [
  "Grand cycle de l'eau",
  "Changement climatique",
  "Usage de la ressource",
  "Risques & réglementation",
  "Gouvernance",
  "Enjeux de qualité",
];

/** Nom de chapitre d'une étape, normalisé : `null` s'il n'y en a pas. */
export function chapterTitleOf(step) {
  const title = (step?.chapter || '').trim();
  return title || null;
}

/**
 * Découpe la séquence en chapitres — un par suite d'étapes consécutives
 * portant le même nom. Rend `[{ title, start, count }]`, `title` valant `null`
 * pour les étapes qu'aucun chapitre ne couvre.
 *
 * Un module sans aucun chapitre rend donc UN groupe sans titre : les appelants
 * n'ont pas de cas particulier à traiter, ils ont juste un chapitre anonyme.
 */
export function buildChapters(steps) {
  const chapters = [];
  (steps || []).forEach((step, index) => {
    const title = chapterTitleOf(step);
    const last = chapters[chapters.length - 1];
    if (last && last.title === title) {
      last.count += 1;
      return;
    }
    chapters.push({ title, start: index, count: 1 });
  });
  return chapters;
}

/**
 * Où tombe une étape dans ce découpage. Rend `null` si l'index ne désigne
 * aucune étape (module vide, index hors bornes).
 */
export function locateStep(chapters, stepIndex) {
  const chapterIndex = chapters.findIndex(
    (chapter) => stepIndex >= chapter.start && stepIndex < chapter.start + chapter.count
  );
  if (chapterIndex < 0) return null;
  const chapter = chapters[chapterIndex];
  return {
    chapter,
    chapterIndex,
    // Rang de l'étape DANS son chapitre, à partir de 1 : c'est ce que la salle
    // suit (« deuxième des quatre écrans sur le climat »), pas son rang absolu.
    stepInChapter: stepIndex - chapter.start + 1,
  };
}

/**
 * Un module est « chapitré » dès qu'au moins une étape porte un nom. Tant que
 * ce n'est pas le cas, l'avancement retombe sur le décompte d'étapes — mieux
 * vaut un compte honnête qu'un chapitre vide qui ferait croire à un découpage.
 */
export function hasChapters(chapters) {
  return chapters.some((chapter) => chapter.title);
}

/** Libellé d'un chapitre dans l'interface, y compris quand il n'a pas de nom. */
export function chapterLabel(chapter, chapterIndex) {
  return chapter.title || `Chapitre ${chapterIndex + 1}`;
}

/**
 * Noms de chapitres déjà employés dans le module, dans l'ordre où ils
 * apparaissent. Sert au builder : ce que l'auteur a déjà écrit passe avant ce
 * que nous suggérons.
 */
export function usedChapterTitles(steps) {
  const seen = [];
  (steps || []).forEach((step) => {
    const title = chapterTitleOf(step);
    if (title && !seen.includes(title)) seen.push(title);
  });
  return seen;
}
